import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxSearch,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/Combobox'
import { type ControlSize } from '@/components/ui/control'
import {
  useCreateParticipant,
  useParticipants,
} from '@/features/directories/api/useDirectory'
import {
  ParticipantFormSheet,
  type ParticipantDraft,
} from '@/features/directories/components/DirectoryFormSheets'
import type { DirectoryParticipant, ParticipantRole } from '@/types/domain'

/* Who is moving a leg — picked from the directory rather than typed (FR-40).
 *
 * ── Which of the seven types, and why only two ──
 *
 * A leg is carried by a COUNTER-AGENT (the forwarder or line whose service was
 * bought for this segment) or by a VEHICLE OWNER (the company whose truck it
 * is). The other five are parties to the shipment rather than movers of it: a
 * shipper consigns cargo, a consignee receives it, a dispatcher and a driver
 * work for whoever owns the vehicle, and an `agent` is our own side of the
 * relationship. Offering all seven would make "carrier" mean "any company
 * mentioned on this case", which is what the source workbook's single conflated
 * column meant and the reason FR-40 split it into seven.
 *
 * The TYPE is shown on every row, because the same company is frequently both —
 * a haulier who also brokers — and which record was picked decides which
 * relationship the leg is recorded against.
 *
 * ── What is offered, and what merely stays selected ──
 *
 * The list offers ACTIVE participants. A deactivated counterparty is one the
 * desk has decided not to work with again, and a chooser that kept offering
 * them would make deactivation cosmetic — the same rule `useTransportRefs`
 * applies with its `activeOnly` flag.
 *
 * Two things are nonetheless kept SELECTED when they are already on the record,
 * because nothing here may reject data that is already saved:
 *
 *   * a linked participant who has since been deactivated. The movement
 *     happened; the row says so and is marked "inactive".
 *   * a free-text carrier matching no participant at all — every leg recorded
 *     before migration 0046, and every carrier written off a waybill before
 *     anybody created a record for them. It is offered as its own first row and
 *     marked "as entered", which is exactly what CountryDropdown does with a
 *     port name stored before that field became a picker.
 *
 * ── The add-a-counterparty escape hatch ──
 *
 * Inside the picker rather than beside it, and it is the difference between
 * this control being used and being worked around. The carrier standing in
 * front of somebody at 7pm is frequently not in the directory yet, and a picker
 * that makes them abandon the leg, cross to the directory screen and start the
 * leg again is a picker whose users save legs with no carrier. The leg sheet
 * stays open and holding its draft throughout — the participant form is a
 * second sheet over the first, not a navigation.
 */

/* The two of FR-40's seven that actually move cargo. Deliberately NOT exported:
   a non-component export from a component file breaks fast refresh for the
   whole module (react-refresh/only-export-components), and nothing outside this
   file needs the list — the picker and the create form it opens are both here,
   which is what keeps them from disagreeing about which types are offered. */
const CARRIER_TYPES: ParticipantRole[] = ['counter-agent', 'vehicle-owner']

/** The value the "carried over" row carries.
 *
 *  A sentinel rather than the name itself, so that `onChange` can tell
 *  "the user re-picked the legacy value" from "the user picked a real
 *  participant". It cannot collide with any row beside it because every other
 *  id in this list is a participant UUID and this is not one.
 *
 *  It was written with a leading NUL, on the reasoning that no real value can
 *  contain one. True, and the wrong trade: a NUL anywhere in a file makes git
 *  treat the WHOLE file as binary, so this component had no readable diff in
 *  review — a large price for a guarantee "not a UUID" already gives. */
const AS_ENTERED = 'as-entered'

export interface CarrierSelectProps {
  /** The linked participant's id, or '' when the leg is linked to nobody. */
  value: string
  /** The carrier's name as the record carries it — the linked participant's,
   *  or the free text from before the link. What the trigger shows when the
   *  list has not loaded yet, and what the "as entered" row offers. */
  name: string
  /** Both halves move together, always. A caller that could set the id without
   *  the name would be a caller that can leave the two disagreeing, and the
   *  name is what a leg falls back to when the directory row is later deleted. */
  onChange: (carrier: { id: string; name: string }) => void
  id?: string
  disabled?: boolean
  size?: ControlSize
  'aria-describedby'?: string
  'aria-invalid'?: boolean
}

/* Base UI: none directly — composed from Combobox.tsx, which is the
   `@base-ui/react` combobox primitive wearing this system's tokens. */
export function CarrierSelect({
  value,
  name,
  onChange,
  id,
  disabled,
  size = 'md',
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: CarrierSelectProps) {
  const { t } = useTranslation()

  /* Two queries rather than one unfiltered read of the whole directory. The
     endpoint filters by ONE type, and pulling all seven to drop five of them
     client-side would fetch every shipper and every driver in the book to
     render a list of hauliers. Both are ordinary cached queries, so the second
     leg sheet opened in a session issues neither. */
  const counterAgents = useParticipants('counter-agent')
  const vehicleOwners = useParticipants('vehicle-owner')

  const createParticipant = useCreateParticipant()
  const [adding, setAdding] = useState(false)

  /* The list's own open state, held here rather than left to the primitive,
     for ONE reason: the add-a-counterparty button opens a second sheet, and a
     combobox popup left open sits ABOVE it. Both are portaled to <body>, the
     popup mounts last, and no z-index the sheet can carry wins against a
     sibling later in document order — so the participant form rendered behind
     its own trigger's list, which is what the user saw.

     Closing the list is also the right thing independently of stacking: the
     question it asks ("which of these?") has been answered with "none of them,
     make a new one", and leaving it open behind a form would offer a choice
     that is about to be made for the user anyway. */
  const [listOpen, setListOpen] = useState(false)

  const all: DirectoryParticipant[] = [
    ...(counterAgents.data ?? []),
    ...(vehicleOwners.data ?? []),
  ]
  /* Active, plus whoever is already on this leg. Sorted by name across both
     types rather than grouped by type: the reader knows the company's name and
     is looking for it, and grouping would make them decide which of two lists
     to scan before they could start. */
  const offered = all
    .filter((participant) => participant.isActive || participant.id === value)
    .sort((a, b) => a.name.localeCompare(b.name))

  /** A saved carrier that is a name and not a link: pre-0046, or off a waybill. */
  const asEntered = value === '' && name.trim() !== ''

  const rows = [
    ...(asEntered
      ? [
          {
            id: AS_ENTERED,
            label: name.trim(),
            hint: t('cases.legs.carrierAsEntered'),
            search: name.trim().toLowerCase(),
          },
        ]
      : []),
    ...offered.map((participant) => ({
      id: participant.id,
      label: participant.name,
      hint: participant.isActive
        ? t(`participantRole.${participant.type}`)
        : t('cases.legs.carrierInactive'),
      search: `${participant.name} ${t(`participantRole.${participant.type}`)}`.toLowerCase(),
    })),
  ]

  const byID = new Map(rows.map((row) => [row.id, row]))

  /* What Base UI is holding. The sentinel stands in for the legacy name, so the
     control shows a selected row rather than an empty box over a record that
     does say who carried it. */
  const selected = value !== '' ? value : asEntered ? AS_ENTERED : null

  const pick = (next: string | null) => {
    if (next === null) {
      onChange({ id: '', name: '' })
      return
    }
    if (next === AS_ENTERED) {
      // Re-picking the legacy row changes nothing, deliberately: it keeps the
      // free text and does not invent a link the record never had.
      onChange({ id: '', name: name.trim() })
      return
    }
    const participant = all.find((row) => row.id === next)
    onChange({ id: next, name: participant?.name ?? '' })
  }

  return (
    <>
      <Combobox
        items={rows.map((row) => row.id)}
        value={selected}
        onValueChange={pick}
        itemToStringLabel={(rowID: string) => byID.get(rowID)?.label ?? ''}
        /* One keystroke matches the name AND the type, so "vehicle owner" finds
           the hauliers when somebody is looking for a truck rather than for a
           company they can already name. The default filter sees only the
           label. */
        filter={(rowID: string, query: string) => {
          const needle = query.trim().toLowerCase()
          if (needle === '') return true
          return byID.get(rowID)?.search.includes(needle) ?? false
        }}
        disabled={disabled}
        autoHighlight
        open={listOpen}
        onOpenChange={setListOpen}
      >
        <ComboboxTrigger
          id={id}
          triggerSize={size}
          aria-describedby={describedBy}
          aria-invalid={invalid}
        >
          <ComboboxValue>
            {(rowID: string | null) => {
              /* `name` rather than the row's label, and the fallback matters:
                 the two queries are still in flight on the first paint of a
                 sheet opened on a saved leg, so `byID` is empty and a
                 label-only trigger would flash "select carrier…" over a leg
                 that has one. */
              const label = (rowID ? byID.get(rowID)?.label : '') || name.trim()
              if (!label) {
                return (
                  <span className="text-text-faint truncate">
                    {t('cases.legs.carrierPlaceholder')}
                  </span>
                )
              }
              return <span className="text-text-body truncate">{label}</span>
            }}
          </ComboboxValue>
        </ComboboxTrigger>

        <ComboboxContent>
          <ComboboxSearch placeholder={t('cases.legs.carrierSearch')} />

          <ComboboxEmpty hint={t('common.form.noMatchesHint')}>
            {t('common.form.noMatches')}
          </ComboboxEmpty>

          <ComboboxList>
            {(rowID: string) => {
              const row = byID.get(rowID)
              if (!row) return null
              return (
                <ComboboxItem key={rowID} value={rowID}>
                  <span className="min-w-0 flex-1 truncate">{row.label}</span>
                  {/* The type, one step down and muted — the secondary reading
                      of the same row rather than a second label, exactly as the
                      alpha-2 code reads on a country row. */}
                  <span className="text-text-muted shrink-0 text-[12px]">
                    {row.hint}
                  </span>
                </ComboboxItem>
              )
            }}
          </ComboboxList>

          {/* Pinned under the list rather than offered as a row in it: it is not
              a carrier, and a filter that hid it when nothing matched would hide
              it exactly when it is most wanted. A plain button, not a
              ComboboxItem, so keyboard navigation of the list does not land on
              something that is not a choice. */}
          <div className="border-border-subtle -mx-1 -mb-1 mt-1 border-t px-1 pt-1">
            <button
              type="button"
              onClick={() => {
                setListOpen(false)
                setAdding(true)
              }}
              className="text-brand hover:bg-surface-hover flex w-full cursor-pointer items-center gap-1.5 rounded-[5px] px-2 py-1.5 text-left text-[12.5px]"
            >
              <Plus size={14} aria-hidden />
              {t('cases.legs.carrierAdd')}
            </button>
          </div>
        </ComboboxContent>
      </Combobox>

      {/* The directory's own editor, over the leg sheet rather than instead of
          it. The leg draft is untouched — this sheet is a sibling in the same
          React tree, and `useFormDraft` re-seeds only when the sheet it belongs
          to closes or changes record.

          `creatableTypes` is the same two the picker lists, so the form cannot
          create something the picker would then refuse to show; the first of
          them is what it defaults to, which is the counter-agent. */}
      <ParticipantFormSheet
        open={adding}
        onClose={() => setAdding(false)}
        creatableTypes={CARRIER_TYPES}
        pending={createParticipant.isPending}
        onSubmit={(draft: ParticipantDraft) =>
          createParticipant.mutate(
            {
              type: draft.type,
              name: draft.name.trim(),
              taxId: draft.taxId.trim(),
              registrationId: draft.registrationId.trim(),
              country: draft.country.trim(),
              address: draft.address.trim(),
              contacts: draft.contacts,
            },
            {
              // Selected on the leg the moment it exists, which is the whole
              // point of the escape hatch: somebody who has just typed a
              // company's name must not have to find it in a list afterwards.
              onSuccess: (created) => {
                onChange({ id: created.id, name: created.name })
                setAdding(false)
              },
            },
          )
        }
      />
    </>
  )
}
